import React from 'react';
import {
  RiPlaneLine,
  RiBuilding2Line,
  RiCheckLine,
  RiCheckboxCircleFill,
  RiTeamLine,
  RiSpeedUpLine,
  RiCalendarEventLine,
  RiLoader4Line,
  RiSaveLine,
  RiArrowLeftLine,
  RiMedalLine,
  RiAwardLine,
  RiStarSFill,
} from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/Drawer';
import ImageCarousel from '@components/ui/ImageCarousel';

/**
 * Aircraft-preview drawer for the manual-add wizard. Layout mirrors
 * TailDetailsDrawer (the /tails details view) as closely as makes
 * sense for a not-yet-persisted record:
 *
 *   • Sticky header — tail number + status pill (green when AVAILABLE)
 *   • Sub-headers — model + operator with matching icons
 *   • Full-width 272px photo carousel
 *   • Tier pill (uses tier as a proxy for the /tails TrustScore band —
 *     new aircraft don't have a score yet)
 *   • "About This Aircraft" card — 2×2 tile grid
 *   • "Amenities" card — checkmark list, matches /tails structure
 *   • Operator card — name + location + rating badges
 *   • Sticky footer — Save button (replaces the /tails Shortlist button)
 */
const AircraftPreviewDrawer = ({
  isOpen,
  onClose,
  formState,
  onBack,
  onSave,
  isSaving,
  saveLabel = 'Save',
}) => {
  if (!formState) return null;
  const {
    aircraft = {},
    makeModel = {},
    features = {},
    amenities = {},
    operator = null,
    photos = [],
    description = '',
  } = formState;

  const carouselImages = photos.map((p) => p.previewUrl).filter(Boolean);
  const tailNumber = aircraft.tail_number || '—';
  const isAvailable = (aircraft.current_status || 'AVAILABLE') === 'AVAILABLE';
  const statusText = (aircraft.current_status || 'Available').toString();
  const statusPretty = statusText.charAt(0).toUpperCase() + statusText.slice(1).toLowerCase();
  const modelName = [makeModel.manufacturer, makeModel.model_name].filter(Boolean).join(' ')
    || aircraft.make_model || 'Unknown Model';
  const operatorLocation = [operator?.faa_city, operator?.faa_state].filter(Boolean).join(', ');
  const operatorDisplay = operator
    ? (operatorLocation ? `${operator.name} • ${operatorLocation}` : operator.name)
    : '—';

  const structuredAmenities = [
    amenities.has_wifi && 'Wi-Fi',
    amenities.has_entertainment_system && 'Entertainment System',
    amenities.has_full_galley && 'Full Galley',
    amenities.has_enclosed_lavatory && 'Enclosed Lavatory',
    amenities.has_satellite_phone && 'Satellite Phone',
  ].filter(Boolean);
  const extraAmenities = Array.isArray(amenities.additional_amenities) ? amenities.additional_amenities : [];
  const customFeatures = Array.isArray(features.custom_features) ? features.custom_features : [];
  const amenityRows = [...structuredAmenities, ...extraAmenities, ...customFeatures];

  // Tier → color band pill (mirrors the /tails TrustScore green area).
  const tier = features.tier || 'BASIC';
  const tierClass = tier === 'PLATINUM' || tier === 'GOLD'
    ? 'bg-green-50 border-green-200 text-green-800'
    : tier === 'SILVER'
      ? 'bg-blue-50 border-blue-200 text-blue-800'
      : 'bg-gray-50 border-gray-200 text-gray-700';

  const rangeText = (makeModel.min_range || makeModel.max_range)
    ? `${makeModel.min_range || '?'} - ${makeModel.max_range || '?'} nm`
    : (aircraft.range_nm ? `${aircraft.range_nm} nm` : '—');
  const yearText = aircraft.year || aircraft.mfr_year || '—';
  const seatingText = aircraft.max_passengers
    ? `${aircraft.max_passengers} Passengers`
    : '—';

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DrawerContent
        side="right"
        className="w-full !max-w-2xl overflow-y-auto !p-0 flex flex-col [&>button]:z-20 !z-[260]"
        overlayClassName="!z-[260]"
      >
        <DrawerHeader className="sr-only">
          <DrawerTitle>Aircraft Preview</DrawerTitle>
        </DrawerHeader>

        {/* Sticky header — tail + status pill */}
        <div className="sticky top-0 bg-white/70 backdrop-blur-sm border-0 px-6 pt-4 z-10">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-1"
              >
                <RiArrowLeftLine className="w-3.5 h-3.5" /> Back to edit
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="!text-2xl font-medium text-gray-900">{tailNumber}</h2>
                <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-white whitespace-nowrap border font-normal border-gray-200 text-gray-700">
                  <RiCheckboxCircleFill className={`w-4 h-4 ${isAvailable ? 'text-green-400' : 'text-red-700'}`} />
                  {statusPretty}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Model + operator sub-headers */}
        <div className="px-6">
          <div className="flex items-center gap-1 text-gray-600 mb-1">
            <RiPlaneLine className="w-4 h-4" />
            <span className="text-sm">{modelName}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <RiBuilding2Line className="w-4 h-4" />
            <span className="text-sm">{operatorDisplay}</span>
          </div>
        </div>

        {/* Photo carousel */}
        <div className="px-6 pt-4">
          {carouselImages.length > 0 ? (
            <ImageCarousel
              images={carouselImages}
              aircraft={aircraft}
              alt={tailNumber}
              className="h-[272px]"
            />
          ) : (
            <div className="w-full h-[272px] rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
              <RiPlaneLine className="w-10 h-10" />
            </div>
          )}
        </div>

        {/* Tier band — mirrors the /tails TrustScore green pill spot */}
        <div className="px-6 py-4">
          <div className={`w-full rounded-full border py-4 px-5 flex items-center gap-3 ${tierClass}`}>
            <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center flex-shrink-0">
              <RiStarSFill className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">Aircraft Tier</div>
              <div className="text-sm font-semibold">{tier}</div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {features.no_aog && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700">
                  <RiCheckLine className="w-3 h-3" /> NO AOG
                </span>
              )}
              {features.has_wingman && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-white/70 text-gray-700">
                  <RiAwardLine className="w-3 h-3" /> WINGMAN
                </span>
              )}
              {operator?.argus_rating && operator.argus_rating !== 'No' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-white/70 text-gray-700 uppercase">
                  <RiMedalLine className="w-3 h-3" /> {operator.argus_rating}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 space-y-3">
          {/* About This Aircraft — 2×2 tile card matching /tails */}
          <div className="border border-gray-200 rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-gray-600 mb-4">ABOUT THIS AIRCRAFT</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <RiPlaneLine className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Aircraft Type</div>
                  <div className="text-xs md:text-sm font-medium text-gray-900">{makeModel.aircraft_type || '—'}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <RiTeamLine className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Seating Capacity</div>
                  <div className="text-xs md:text-sm font-medium text-gray-900">{seatingText}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <RiSpeedUpLine className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Range</div>
                  <div className="text-xs md:text-sm font-medium text-gray-900">{rangeText}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <RiCalendarEventLine className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Year of Manufacture</div>
                  <div className="text-xs md:text-sm font-medium text-gray-900">{yearText}</div>
                </div>
              </div>
            </div>
          </div>

          {/* About the aircraft (description) — only when non-empty */}
          {description && (
            <div className="border border-gray-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">DESCRIPTION</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{description}</p>
            </div>
          )}

          {/* Amenities card — check-list layout matches /tails */}
          <div className="border border-gray-200 rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-gray-600 mb-4">AMENITIES</h3>
            {amenityRows.length > 0 ? (
              <div className="grid grid-cols-1 gap-0">
                {amenityRows.map((label, index) => (
                  <div key={`${label}-${index}`} className="flex">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <RiCheckLine className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <div className="text-gray-900 mt-2 text-sm">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No amenities included.</p>
            )}
          </div>

          {/* Operator card */}
          {operator && (
            <div className="border border-gray-200 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">OPERATOR</h3>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-base truncate">{operator.name}</div>
                  <div className="text-sm text-gray-500 truncate">
                    {(operator.faa_city || '').replace(/\b\w/g, (c) => c.toUpperCase())}
                    {operator.faa_state && `, ${operator.faa_state.toUpperCase()}`}
                  </div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <RiBuilding2Line className="w-6 h-6 text-gray-500" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Save footer — Save replaces the /tails Shortlist button.
            Buttons occupy the full width, split 50/50, matching the
            wizard's own Back/Next footer for visual consistency. */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSaving}
            className="flex-1 px-4 py-3 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 px-4 py-3 rounded-full bg-primary-700 hover:bg-primary-800 disabled:bg-gray-300 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {isSaving ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiSaveLine className="w-4 h-4" />}
            {isSaving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AircraftPreviewDrawer;
